/**
 * No-break word list, shared by the whole group (D1 table nobreak_words, /api/words).
 * The last list fetched is remembered on this device so text wraps the same way offline.
 * Adding and removing need the server; every refusal comes back with its reason.
 */

const CACHE_KEY = 'craftstudio-nobreak-group';   // last list fetched from the server
const LEGACY_KEY = 'craftstudio-nobreak';        // the old per-device list (before the group list)
const IMPORT_DONE_KEY = 'craftstudio-nobreak-import-done';
const DEFAULT_WORDS = ['ส่งออก'];

export interface WordEntry { word: string; addedAt: string; addedBy: { id: string; name: string } | null }

let entries: WordEntry[] = loadCache();
let words: string[] = entries.length ? entries.map(e => e.word) : [...DEFAULT_WORDS];

function store(key: string, value: string | null) {
  try { if (value == null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch { /* storage blocked: the list is still used for this session */ }
}
function read(key: string): unknown {
  try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : null; } catch { return null; }
}

function loadCache(): WordEntry[] {
  const v = read(CACHE_KEY);
  return Array.isArray(v) && v.every(e => e && typeof e.word === 'string') ? v as WordEntry[] : [];
}

export const getWords = (): readonly string[] => words;
export const getEntries = (): readonly WordEntry[] => entries;
export const hasServerList = () => entries.length > 0;

/** Returns a Thai error message, or null when the word can be added. Same rules as the server (worker/rules.ts checkWord). */
export function validateWord(raw: string, list: readonly string[]): string | null {
  const w = raw.normalize('NFC').trim();
  if (!w) return 'พิมพ์คำที่ต้องการก่อนกดเพิ่ม';
  if (/\s/.test(w)) return 'คำห้ามตัดต้องไม่มีช่องว่าง ถ้าเป็นหลายคำให้เพิ่มทีละคำ';
  if ([...w].length > 40) return 'คำยาวเกิน 40 ตัวอักษร ลองแบ่งเป็นคำที่สั้นลง';
  if (list.includes(w)) return `"${w}" อยู่ในรายการแล้ว`;
  return null;
}

export interface ListChange { added: WordEntry[]; removed: string[] }

/**
 * Take the list just fetched from the server. Returns what changed compared with the list this device
 * had before (nothing on the very first fetch, since there is nothing to compare with).
 */
export function setServerList(next: WordEntry[]): ListChange {
  const first = !entries.length;
  const before = new Set(entries.map(e => e.word));
  const after = new Set(next.map(e => e.word));
  const change: ListChange = first
    ? { added: [], removed: [] }
    : { added: next.filter(e => !before.has(e.word)), removed: [...before].filter(w => !after.has(w)) };
  entries = next;
  words = next.map(e => e.word);
  store(CACHE_KEY, JSON.stringify(next));
  return change;
}

/** Words in this device's old list that the group list doesn't have yet (until imported or declined). */
export function legacyWordsToImport(): string[] {
  if (read(IMPORT_DONE_KEY)) return [];
  const v = read(LEGACY_KEY);
  if (!Array.isArray(v)) return [];
  const have = new Set(words);
  return [...new Set(v.filter((w): w is string => typeof w === 'string').map(w => w.normalize('NFC').trim()))]
    .filter(w => w && !have.has(w));
}

/** The old list was imported or the person said no: don't ask again on this device. */
export function finishLegacyImport() { store(IMPORT_DONE_KEY, JSON.stringify(true)); }
